import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, RefreshCw, Check, X, ArrowLeft, LogOut, Users, Trash2, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  ADMIN_TOKEN_KEY,
  ADMIN_ROLE_KEY,
  adminLogin,
  adminStats,
  adminListRefunds,
  adminApproveRefund,
  adminRejectRefund,
  adminSendNewsletter,
  adminListUsers,
  adminCreateUser,
  adminDeleteUser,
} from "@/lib/api";

const STATUS_TABS = [
  { key: "pending_processing", label: "Pending" },
  { key: "refunded", label: "Refunded" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const badge = (s) => {
  const map = {
    pending_processing: "bg-yellow-500/15 text-yellow-300",
    refunded: "bg-[var(--neon-cyan)]/15 text-[var(--neon-cyan)]",
    rejected: "bg-white/10 text-white/50",
    failed: "bg-red-500/15 text-red-300",
  };
  return map[s] || "bg-white/10 text-white/60";
};

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { token, role } = await adminLogin(email, password);
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
      localStorage.setItem(ADMIN_ROLE_KEY, role);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-[#121118] border border-white/10 p-8" data-testid="admin-login">
        <div className="w-12 h-12 rounded-xl bg-[var(--neon-cyan)]/15 flex items-center justify-center mb-6">
          <ShieldCheck className="text-[var(--neon-cyan)]" />
        </div>
        <h1 className="font-future text-xl">Admin sign in</h1>
        <p className="text-white/50 text-sm mt-1 mb-6">Refund & operations console</p>
        <label className="label-caps text-white/50">Email</label>
        <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/5 border-white/15 text-white h-12 mt-1 mb-4" />
        <label className="label-caps text-white/50">Password</label>
        <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white/5 border-white/15 text-white h-12 mt-1 mb-6" />
        <button data-testid="admin-login-btn" disabled={busy} className="w-full label-caps py-3.5 rounded-full bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300 flex items-center justify-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function TeamManager() {
  const [users, setUsers] = useState([]);
  const [meEmail, setMeEmail] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("write");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await adminListUsers();
      setUsers(data.users);
      setMeEmail(data.me?.email || "");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Enter a valid email");
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await adminCreateUser(email.toLowerCase(), password, role);
      toast.success(`Added ${email} (${role})`);
      setEmail(""); setPassword(""); setRole("write");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add admin");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (em) => {
    try {
      await adminDeleteUser(em);
      toast.success(`Removed ${em}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const isOwnerRole = (rl) => ["owner", "admin"].includes(rl);

  return (
    <div className="rounded-2xl bg-[#121118] border border-white/10 p-6 mb-10" data-testid="team-manager">
      <div className="flex items-center gap-2 mb-1">
        <Users size={18} className="text-[var(--neon-cyan)]" />
        <h2 className="font-future text-lg">Team & access</h2>
      </div>
      <p className="text-white/40 text-sm mb-5">Add admins and set read or read/write access. Only the owner can manage the team.</p>

      <div className="space-y-2 mb-6">
        {users.map((u) => (
          <div key={u.email} className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3" data-testid={`user-row-${u.email}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm text-white/85 truncate">{u.email}</span>
              {u.email === meEmail && <span className="label-caps text-[9px] text-white/40">you</span>}
            </div>
            <div className="flex items-center gap-3">
              <span className={`label-caps text-[9px] px-2 py-1 rounded-full ${isOwnerRole(u.role) ? "bg-[var(--neon-magenta)]/15 text-[var(--neon-magenta)]" : "bg-white/10 text-white/60"}`}>
                {u.role === "write" ? "read/write" : u.role}
              </span>
              {!isOwnerRole(u.role) && (
                <button data-testid={`remove-${u.email}`} onClick={() => remove(u.email)} className="text-white/40 hover:text-red-400 transition-colors" title="Remove">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
        <Input data-testid="new-admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="new-admin@email.com" className="sm:col-span-5 bg-white/5 border-white/15 text-white h-11" />
        <Input data-testid="new-admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Temp password (8+ chars)" className="sm:col-span-4 bg-white/5 border-white/15 text-white h-11" />
        <select
          data-testid="new-admin-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="sm:col-span-2 bg-white/5 border border-white/15 text-white rounded-md h-11 px-3 text-sm"
        >
          <option value="write" className="bg-[#121118]">Read/Write</option>
          <option value="read" className="bg-[#121118]">Read only</option>
        </select>
        <button
          data-testid="add-admin-btn"
          onClick={add}
          disabled={busy}
          className="sm:col-span-1 h-11 rounded-md bg-[var(--neon-cyan)] text-black flex items-center justify-center hover:bg-white transition-colors disabled:opacity-60"
          title="Add admin"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={16} />}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [tab, setTab] = useState("pending_processing");
  const [refunds, setRefunds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [nlTitle, setNlTitle] = useState("");
  const [nlBody, setNlBody] = useState("");
  const [nlSending, setNlSending] = useState(false);
  const role = localStorage.getItem(ADMIN_ROLE_KEY) || "read";
  const canWrite = ["owner", "admin", "write"].includes(role);
  const isOwner = ["owner", "admin"].includes(role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([adminListRefunds(tab), adminStats()]);
      setRefunds(r);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAuthed(false);
      } else {
        toast.error("Could not load data");
      }
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const act = async (id, kind) => {
    setActingId(id);
    try {
      if (kind === "approve") {
        const res = await adminApproveRefund(id);
        toast.success(res.stripe_refund_id ? `Refunded via Stripe (${res.stripe_refund_id})` : "Refund processed");
      } else {
        await adminRejectRefund(id);
        toast.success("Request rejected");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setActingId(null);
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_ROLE_KEY);
    setAuthed(false);
  };

  const sendNewsletter = async () => {
    if (!nlTitle.trim() || !nlBody.trim()) {
      toast.error("Add a title and body first");
      return;
    }
    setNlSending(true);
    try {
      const res = await adminSendNewsletter(nlTitle.trim(), nlBody.trim());
      toast.success(`Newsletter sent to ${res.recipients} subscriber${res.recipients === 1 ? "" : "s"}`);
      setNlTitle("");
      setNlBody("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send newsletter");
    } finally {
      setNlSending(false);
    }
  };

  if (!authed) {
    return (
      <div className="bg-[#0A0A0C] text-white min-h-screen">
        <Login onDone={() => setAuthed(true)} />
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0C] text-white min-h-screen">
      <div className="border-b border-white/10 sticky top-0 z-40 bg-black/50 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 label-caps text-white/60 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Site
          </Link>
          <span className="font-future text-sm flex items-center gap-2">
            Admin console
            <span data-testid="admin-role-badge" className="label-caps text-[9px] px-2 py-0.5 rounded-full bg-[var(--neon-cyan)]/15 text-[var(--neon-cyan)]">{role}</span>
          </span>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 label-caps text-white/60 hover:text-white transition-colors">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <h1 className="font-future text-3xl tracking-tight">Refund requests</h1>
          <button onClick={load} className="flex items-center gap-2 label-caps px-4 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors" data-testid="admin-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-10" data-testid="admin-stats">
            {[
              { k: "pending_refunds", label: "Pending refunds" },
              { k: "subscribers", label: "Newsletter subscribers" },
              { k: "leads", label: "Freemium leads" },
            ].map((c) => (
              <div key={c.k} className="rounded-2xl bg-[#121118] border border-white/10 p-5">
                <p className="font-future text-3xl text-[var(--neon-cyan)]">{stats[c.k]}</p>
                <p className="label-caps text-white/40 mt-2">{c.label}</p>
              </div>
            ))}
          </div>
        )}

        {isOwner && <TeamManager />}

        {/* Newsletter composer */}
        {canWrite && (
        <div className="rounded-2xl bg-[#121118] border border-white/10 p-6 mb-10" data-testid="newsletter-composer">
          <h2 className="font-future text-lg mb-1">Send fortnightly newsletter</h2>
          <p className="text-white/40 text-sm mb-4">Broadcasts to all subscribers via email.</p>
          <Input
            data-testid="nl-title"
            value={nlTitle}
            onChange={(e) => setNlTitle(e.target.value)}
            placeholder="Subject / title"
            className="bg-white/5 border-white/15 text-white h-12 mb-3"
          />
          <textarea
            data-testid="nl-body"
            value={nlBody}
            onChange={(e) => setNlBody(e.target.value)}
            placeholder="Write your update… (one paragraph per line)"
            rows={4}
            className="w-full rounded-lg bg-white/5 border border-white/15 text-white text-sm p-3 placeholder:text-white/30 resize-y"
          />
          <button
            data-testid="nl-send-btn"
            onClick={sendNewsletter}
            disabled={nlSending}
            className="mt-4 label-caps px-6 py-3 rounded-full bg-white text-black hover:bg-[var(--neon-cyan)] transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {nlSending ? <Loader2 size={15} className="animate-spin" /> : "Send to subscribers"}
          </button>
        </div>
        )}

        <div className="flex gap-2 mb-6">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`label-caps px-4 py-2 rounded-full transition-colors ${tab === t.key ? "bg-white text-black" : "text-white/50 hover:text-white border border-white/15"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="refunds-table">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-white/[0.03] label-caps text-white/40 text-[10px]">
            <div className="col-span-3">Email</div>
            <div className="col-span-3">Product</div>
            <div className="col-span-1">%</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Action</div>
          </div>

          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
          ) : refunds.length === 0 ? (
            <div className="p-10 text-center text-white/40 text-sm" data-testid="refunds-empty">No requests in this view.</div>
          ) : (
            refunds.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-t border-white/5 items-center text-sm" data-testid={`refund-row-${r.id}`}>
                <div className="col-span-3 truncate text-white/80">{r.email}</div>
                <div className="col-span-3 truncate text-white/60">{r.product_name}</div>
                <div className="col-span-1 font-mono2 text-white/70">{r.percent}%</div>
                <div className="col-span-2 font-future text-white">SFr {Number(r.amount).toFixed(2)}</div>
                <div className="col-span-1">
                  <span className={`label-caps px-2 py-1 rounded-full text-[9px] ${badge(r.status)}`}>{(r.status || "").replace("_processing", "")}</span>
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  {r.status === "pending_processing" && canWrite ? (
                    <>
                      <button
                        data-testid={`approve-${r.id}`}
                        disabled={actingId === r.id}
                        onClick={() => act(r.id, "approve")}
                        className="w-9 h-9 rounded-lg bg-[var(--neon-cyan)] text-black flex items-center justify-center hover:bg-white transition-colors disabled:opacity-50"
                        title="Approve & refund via Stripe"
                      >
                        {actingId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={16} />}
                      </button>
                      <button
                        data-testid={`reject-${r.id}`}
                        disabled={actingId === r.id}
                        onClick={() => act(r.id, "reject")}
                        className="w-9 h-9 rounded-lg border border-white/20 text-white/60 flex items-center justify-center hover:text-white hover:border-white/40 transition-colors disabled:opacity-50"
                        title="Reject"
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <span className="font-mono2 text-[10px] text-white/30">{r.stripe_refund_id ? "via Stripe" : "—"}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <p className="mt-6 font-mono2 text-[11px] text-white/35">
          Approving issues a real refund to the original payment method via Stripe for the recorded amount.
        </p>
      </div>
    </div>
  );
}
